import React, { useState } from 'react';
// import './ShopStatistics.css'; // Remove CSS import
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
    Area, AreaChart, ComposedChart
} from 'recharts';
import {
    Row, Col, Card, Statistic, Spin, Segmented, Typography, Space, Divider, Alert
} from 'antd';
import {
    EyeOutlined, StarOutlined, LikeOutlined, TagOutlined, LinkOutlined // Example icons for stats
} from '@ant-design/icons';

const { Title, Text } = Typography;

const ShopStatistics = ({ aggregatedStats }) => {
    // Remove local state for data that now comes from props
    // const [viewsData, setViewsData] = useState([]);
    // const [reviewsData, setReviewsData] = useState([]);
    // const [brandData, setBrandData] = useState([]);
    
    // Keep timeframe state
    const [timeFrame, setTimeFrame] = useState('month'); // Or maybe this should come from parent/props too?

    // Remove loading and error state, as the parent (DashboardPage) handles loading/error for aggregatedStats
    // const [loading, setLoading] = useState(true);
    // const [error, setError] = useState(null);

    // Color palettes remain the same
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
    const RATING_COLORS = ['#FF5252', '#FF9800', '#FFC107', '#8BC34A', '#4CAF50'];

    // Remove useEffect and generateMockData, as data comes directly from aggregatedStats
    // useEffect(() => { ... }, [aggregatedStats, timeFrame]);
    // const generateMockData = (period) => { ... };

    // Get data directly from props
    const viewsData = aggregatedStats?.viewsData || [];
    const reviewsData = aggregatedStats?.reviewsData || [];
    const brandData = aggregatedStats?.brandData || [];
    const totalClicks = aggregatedStats?.totalTrackedClicks || 0;
    const totalViews = aggregatedStats?.totalViews || 0;
    const totalRatings = aggregatedStats?.totalReviews || reviewsData.reduce((sum, item) => sum + item.value, 0);

    // NEU: Passe die reviewsData an, um Prozentsätze im Namen für die Legende zu enthalten
    const reviewsDataForLegend = reviewsData.map(item => ({
        ...item,
        name: `${item.name} (${totalRatings > 0 ? Math.round((item.value / totalRatings) * 100) : 0}%)` 
    }));

    // Basic check if essential data is missing (though parent should ideally handle this)
    if (!aggregatedStats) {
        return <Alert message="Keine Statistikdaten zum Anzeigen vorhanden." type="warning" showIcon />;
    }

    // --- Custom Tooltips remain the same, but use the data from props --- 
    const ViewsTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip" style={{backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px'}}> 
                    <p style={{fontWeight: 'bold', marginBottom: '5px'}}>{label}</p>
                    {/* Check if payload exists before accessing properties */} 
                    {payload[0] && <p style={{margin: '2px 0'}}>Aufrufe: <Text strong>{payload[0].value}</Text></p>}
                    {payload[1] && <p style={{margin: '2px 0'}}>Besucher: <Text strong>{payload[1].value}</Text></p>}
                 </div>
            );
        }
        return null;
    };

    const RatingTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload; // recharts structure
            return (
                 <div className="custom-tooltip" style={{backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px'}}> 
                    <p style={{fontWeight: 'bold', marginBottom: '5px'}}>{data.name}</p>
                    <p style={{margin: '2px 0'}}>Anzahl: <Text strong>{data.value}</Text></p>
                     {totalRatings > 0 && (
                         <p style={{margin: '2px 0'}}>Prozent: <Text strong>{Math.round((data.value / totalRatings) * 100)}%</Text></p>
                     )}
                </div>
            );
        }
        return null;
    };

    const BrandTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload; // recharts structure
            return (
                 <div className="custom-tooltip" style={{backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px'}}> 
                    <p style={{fontWeight: 'bold', marginBottom: '5px'}}>{data.name}</p>
                     {/* Ensure value exists before accessing */}
                     {payload[0] && <p style={{margin: '2px 0'}}>Interesse: <Text strong>{payload[0].value}</Text></p>}
                 </div>
            );
        }
        return null;
    };
    // --- End of Tooltips --- 

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Header Section - Maybe remove timeframe selector if backend doesn't support it yet? */}
            <Row justify="space-between" align="middle">
                <Col>
                    <Title level={4} style={{ margin: 0 }}>Detail-Statistiken</Title> 
                </Col>
                 <Col>
                    {/* Keep timeframe selector for now, although backend only returns one timeframe */}
                     <Segmented
                         options={[
                             { label: 'Letzte 30 Tage', value: 'month' }, // Adjust label if needed
                            // { label: 'Woche', value: 'week' },
                            // { label: 'Jahr', value: 'year' },
                         ]}
                         value={timeFrame} // Keep state for potential future use
                         onChange={(value) => setTimeFrame(value)} // Keep state change for potential future use
                     />
                 </Col>
             </Row>

            {/* Charts Section - Now uses data from props */}
            <Row gutter={[16, 16]}>
                 {/* NEU: Aufrufe und Klicks nebeneinander */}
                 <Col xs={24} lg={12}>
                     <Card title="Shop-Aufrufe & Klicks" bordered={false}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Statistic 
                                    title="Profilaufrufe (Intern)" 
                                    value={totalViews} 
                                    prefix={<EyeOutlined />} 
                                />
                            </Col>
                            <Col span={12}>
                                 <Statistic 
                                    title="Website-Klicks (Extern)" 
                                    value={totalClicks} 
                                    prefix={<LinkOutlined />} // Anderes Icon für Klicks
                                />
                            </Col>
                         </Row>
                         {/* Optional: Platz für das detaillierte Views-Diagramm, falls viewsData später kommt */}
                         {/* {viewsData.length > 0 && (...) } */}
                     </Card>
                 </Col>

                 {/* Rating Distribution Chart */}
                 <Col xs={24} lg={12}>
                     <Card title="Bewertungsverteilung" bordered={false}>
                        {totalRatings > 0 ? ( // Prüfe auf totalRatings statt auf reviewsData.reduce
                             <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        // Verwende die angepassten Daten für die Legende
                                        data={reviewsDataForLegend} 
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                        nameKey="name"
                                        // Entferne das Label auf den Kuchenstücken
                                        label={false}
                                    >
                                        {/* Farben bleiben gleich (verwende originale reviewsData oder reviewsDataForLegend hier, ist egal) */}
                                        {reviewsDataForLegend.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color || RATING_COLORS[index % RATING_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    {/* Tooltip bleibt */}
                                    <Tooltip content={<RatingTooltip />} /> 
                                     {/* Standard-Legende zeigt jetzt Namen mit Prozenten an */}
                                    <Legend />
                                </PieChart>
                             </ResponsiveContainer>
                        ) : (
                            <Text type="secondary">Keine Bewertungsdaten verfügbar.</Text>
                        )}
                     </Card>
                 </Col>

                 {/* Brand Popularity Chart */}
                 <Col xs={24} lg={brandData.length > 0 ? 12 : 0} style={{display: brandData.length > 0 ? 'block' : 'none'}}>
                     <Card title="Markeninteresse" bordered={false}>
                         {brandData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={brandData} layout="vertical" margin={{left: 30}}> 
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" allowDecimals={false}/>
                                    <YAxis type="category" dataKey="name" width={100} interval={0} />
                                    <Tooltip content={<BrandTooltip />} />
                                    <Legend />
                                    <Bar dataKey="interests" name="Nennungen" fill="#82ca9d">
                                        {brandData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                             </ResponsiveContainer>
                         ) : (
                            <Text type="secondary">Keine Markendaten verfügbar.</Text>
                        )}
                     </Card>
                 </Col>
             </Row>
         </Space>
    );
};

export default ShopStatistics;